import React from 'react';
import { InputGroup, Button, FloatingLabel, Row, Col } from 'react-bootstrap';
import { Formik, Form, useFormikContext } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { map } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'usehooks-ts';

import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClaimStatuses, tClaimStatus, BillingTypeCodes, BillingTypeCodeToTypeMapping } from '@models/InsuranceClaim';
import { FormikControl, FormikSelect } from '@components/Base';
import { mergeInitialValues } from '@utils/formik';

type FormValues = {
  startDate: string;
  endDate: string;
  status: ClaimStatuses | '';
  billingType: string;
  search: string;
};

const FormForm = () => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const { values } = useFormikContext<FormValues>();
  const { startDate, endDate, status, billingType } = values;

  useUpdateEffect(() => {
    setSearchParams(values);
  }, [startDate, endDate, status, billingType]);

  return (
    <Form>
      <Row className="align-items-end">
        <Col xs={12} xl={4} className="mb-3">
          <InputGroup>
            <FloatingLabel label={t('insuranceClaim.search')} style={{ flex: '1 1 auto', width: '1%', minWidth: '0' }}>
              <FormikControl name="search" isValid={false} isInvalid={false} />
            </FloatingLabel>
            <Button type="submit">
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup>
        </Col>

        <Col xs={6} xl={2} className="mb-3">
          <FloatingLabel label={t('claim.startDate')} >
            <FormikControl name="startDate" type="date" isValid={false} isInvalid={false} />
          </FloatingLabel>
        </Col>

        <Col xs={6} xl={2} className="mb-3">
          <FloatingLabel label={t('claim.endDate')} >
            <FormikControl name="endDate" type="date" isValid={false} isInvalid={false} />
          </FloatingLabel>
        </Col>

        <Col xs={6} xl={2} className="mb-3">
          <FloatingLabel label={t('common:status')}>
            <FormikSelect name="status" isValid={false} isInvalid={false}>
              <option value="">{t('common:all')}</option>
              {map(ClaimStatuses, (value) => (
                <option key={value} value={value}>
                  {tClaimStatus(value)}
                </option>
              ))}
            </FormikSelect>
          </FloatingLabel>
        </Col>

        <Col xs={6} xl={2} className="mb-3">
          <FloatingLabel label={t('common:billingType')}>
            <FormikSelect name="billingType" isValid={false} isInvalid={false}>
              <option value="">{t('common:all')}</option>
              {map(BillingTypeCodes, (value) => (
                <option key={value} value={BillingTypeCodeToTypeMapping[value]}>
                  {value}
                </option>
              ))}
            </FormikSelect>
          </FloatingLabel>
        </Col>
      </Row>
    </Form >
  );
}

const ClaimControlForm = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams);
  const defaultValues: FormValues = { status: '', search: '', startDate: '', endDate: '', billingType: '' };
  const initialValues = mergeInitialValues(defaultValues, params);

  const handleSubmit = ({ search }: FormValues) => {
    setSearchParams({ search });
  }

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
      <FormForm />
    </Formik>
  );
};

export { ClaimControlForm };
